'use client'

import { useFormStatus } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinnerThird } from '@awesome.me/kit-94e593bf23/icons/classic/light'

/**
 * Reusable submit button component with pending state and spinner
 *
 * Automatically detects form submission via useFormStatus (React 19).
 * Can also be controlled manually via isPending prop.
 *
 * For minimum spinner duration, wrap your action with withMinDuration():
 *   const response = await withMinDuration(myAction(data), 800)
 *
 * @param {boolean} props.isPending - Manual pending state override
 * @param {string} props.pendingText - Text during pending (default: "Saving...")
 * @param {string} props.children - Button text for normal state
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.form - Form id for buttons outside forms
 * @param {Object} props.icon - FontAwesome icon for left side
 * @param {string} props.size - 'sm' for small button
 */
const SubmitWithPendingButton = ({
    isPending: isPendingProp = false,
    pendingText = 'Saving...',
    children,
    className = '',
    disabled = false,
    form,
    icon,
    size,
    ...props
}) => {
    // React 19's useFormStatus auto-detects form submission
    const { pending: formPending } = useFormStatus()

    // Pending if either manual prop or form is submitting
    const isPending = isPendingProp || formPending

    const sizeClass = size === 'sm' ? 'btn-sm' : ''

    return (
        <button
            type="submit"
            disabled={isPending || disabled}
            form={form}
            className={`btn btn-primary w-auto flex items-center justify-center ${sizeClass} ${isPending || disabled ? 'opacity-75' : ''} ${className}`}
            {...props}
        >
            {isPending ? (
                <>
                    <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                    {pendingText}
                </>
            ) : (
                <>
                    {icon && <FontAwesomeIcon icon={icon} className="mr-1" />}
                    {children}
                </>
            )}
        </button>
    )
}

export default SubmitWithPendingButton
